/**
 * Remember last successful login (email + IdP) in localStorage and offer
 * a one-tap re-login prompt when the visitor is anonymous.
 *
 * Google IdP: try GIS One Tap first; fall back to /account/login/ auto-click.
 * ParkRanger IdP: navigate to login and open the password overlay (email prefilled).
 */


!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{},n=(new e.Error).stack;n&&(e._sentryDebugIds=e._sentryDebugIds||{},e._sentryDebugIds[n]="3fb305dd-514f-556a-9560-1fe9d2cc3fc8")}catch(e){}}();
const STORAGE_KEY = 'pr.lastLogin';
const DISMISS_KEY = 'pr.lastLogin.dismissed';
const AUTO_KEY = 'pr.lastLogin.pendingAuto';

const GSI_CLIENT_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_ONETAP_LOGIN = '/account/login/google-onetap/';
/** How long to wait for GIS to report display/skip before falling back. */
const ONE_TAP_PROMPT_TIMEOUT_MS = 6000;

const IDP_LABELS = {
    google: 'Google',
    parkranger: 'ParkRanger',
};

/** @type {Promise<void> | null} */
let gsiLoadPromise = null;

/** Session-expired panel was dismissed on this page load. */
let reauthDismissed = false;

/**
 * @returns {Storage | null}
 */
function getStorage() {
    try {
        if (window.park_ranger_storage) {
            return window.park_ranger_storage;
        }
        if (window.localStorage) {
            return window.localStorage;
        }
    } catch (_err) {
        /* private mode / quota */
    }
    return null;
}

/**
 * @returns {Storage | null}
 */
function getSessionStorage() {
    try {
        if (window.sessionStorage) {
            return window.sessionStorage;
        }
    } catch (_err) {
        /* private mode */
    }
    return null;
}

/**
 * Normalize IdP names to the Profile.idp values (google | parkranger).
 * @param {string | null | undefined} idp
 * @returns {string}
 */
export function normalizeIdp(idp) {
    const raw = String(idp || '').trim().toLowerCase();
    if (!raw) {
        return '';
    }
    if (raw.startsWith('google')) {
        return 'google';
    }
    if (raw === 'parkranger' || raw === 'pr' || raw === 'local') {
        return 'parkranger';
    }
    return raw;
}

/**
 * @param {string} idp
 * @returns {string}
 */
export function idpDisplayName(idp) {
    const key = normalizeIdp(idp);
    return IDP_LABELS[key] || key || 'your account';
}

/**
 * @returns {{email: string, idp: string, savedAt: number} | null}
 */
export function getLastLogin() {
    const storage = getStorage();
    if (!storage) {
        return null;
    }
    try {
        const raw = storage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        const email = String(parsed?.email || '').trim();
        const idp = normalizeIdp(parsed?.idp);
        if (!email || !idp) {
            return null;
        }
        return {
            email,
            idp,
            savedAt: Number(parsed?.savedAt) || 0,
        };
    } catch (_err) {
        return null;
    }
}

/**
 * Persist last successful login for re-prompting on later visits.
 * @param {{email?: string | null, idp?: string | null}} opts
 */
export function saveLastLogin({email = null, idp = null} = {}) {
    const storage = getStorage();
    if (!storage) {
        return;
    }
    const cleanEmail = String(email || '').trim();
    const cleanIdp = normalizeIdp(idp);
    if (!cleanEmail || !cleanIdp) {
        return;
    }
    try {
        storage.setItem(STORAGE_KEY, JSON.stringify({
            email: cleanEmail,
            idp: cleanIdp,
            savedAt: Date.now(),
        }));
        const session = getSessionStorage();
        session?.removeItem(DISMISS_KEY);
    } catch (_err) {
        /* ignore quota / private mode */
    }
}

/**
 * Mark the re-login prompt dismissed for this browser tab/session.
 */
export function dismissLastLoginPrompt() {
    const session = getSessionStorage();
    try {
        session?.setItem(DISMISS_KEY, '1');
    } catch (_err) {
        /* ignore */
    }
}

/**
 * @returns {boolean}
 */
function isPromptDismissed() {
    const session = getSessionStorage();
    try {
        return session?.getItem(DISMISS_KEY) === '1';
    } catch (_err) {
        return false;
    }
}

/**
 * Request auto IdP click after navigating to /account/login/.
 */
export function armLastLoginAuto() {
    const session = getSessionStorage();
    try {
        session?.setItem(AUTO_KEY, '1');
    } catch (_err) {
        /* ignore */
    }
}

/**
 * Consume the one-shot auto-login flag.
 * @returns {boolean}
 */
export function consumeLastLoginAuto() {
    const session = getSessionStorage();
    try {
        if (session?.getItem(AUTO_KEY) !== '1') {
            return false;
        }
        session.removeItem(AUTO_KEY);
        return true;
    } catch (_err) {
        return false;
    }
}

/**
 * @returns {boolean}
 */
function isOnLoginPage() {
    return /^\/account\/login\/?$/.test(location.pathname);
}

/**
 * Persist from the authenticated page globals when available.
 */
export function syncLastLoginFromAuthenticatedSession() {
    if (!window.username) {
        return;
    }
    const email = window.user_email || window.username || '';
    const idp = window.profile_idp || '';
    if (email && idp) {
        saveLastLogin({email, idp});
    }
}

/**
 * @param {string} [nextPath]
 * @returns {string}
 */
function buildLoginUrl(nextPath) {
    const loginUrl = new URL('/account/login/', location.origin);
    if (nextPath && nextPath !== '/' && !nextPath.startsWith('/account/login')) {
        loginUrl.searchParams.set('next', nextPath);
    }
    return loginUrl.pathname + loginUrl.search;
}

/**
 * Navigate to /account/login/ with pending auto-IdP armed.
 * @param {string} [nextPath]
 */
function goToLoginWithAuto(nextPath) {
    armLastLoginAuto();
    dismissLastLoginPrompt();
    location.assign(buildLoginUrl(nextPath));
}

/**
 * Load Google Identity Services client once.
 * GSI copies document.currentScript.nonce onto styles it injects; without a
 * nonce on this script tag, One Tap hits style-src-elem CSP even when the
 * content hash is allowlisted (nonce in the policy disables bare unsafe-inline).
 * @returns {Promise<void>}
 */
function loadGsiClient() {
    if (window.google?.accounts?.id) {
        return Promise.resolve();
    }
    if (gsiLoadPromise) {
        return gsiLoadPromise;
    }
    gsiLoadPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${GSI_CLIENT_SRC}"]`);
        if (existing) {
            existing.addEventListener('load', () => resolve(), {once: true});
            existing.addEventListener('error', () => reject(new Error('GSI load failed')), {once: true});
            if (window.google?.accounts?.id) {
                resolve();
            }
            return;
        }
        const script = document.createElement('script');
        script.src = GSI_CLIENT_SRC;
        script.async = true;
        const nonce = window.jsnonce || '';
        if (nonce) {
            script.nonce = nonce;
            script.setAttribute('nonce', nonce);
        }
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('GSI load failed'));
        document.head.appendChild(script);
    }).catch((err) => {
        gsiLoadPromise = null;
        throw err;
    });
    return gsiLoadPromise;
}

/**
 * Try Google One Tap. Resolves true if One Tap took over (prompt shown or
 * session established in-place). Resolves false when GIS cannot show a
 * prompt so the caller should fall back to /account/login/.
 *
 * Uses the JS callback → JSON POST to /account/login/google-onetap/ so the
 * Django session is set and the adminbar Login control is swapped for the
 * avatar menu without a full page reload.
 *
 * @param {{email?: string, nextPath?: string}} opts
 * @returns {Promise<boolean>}
 */
export function tryGoogleOneTap({email = '', nextPath = '/'} = {}) {
    const clientId = String(window.google_oauth_client_id || '').trim();
    if (!clientId) {
        return Promise.resolve(false);
    }

    return loadGsiClient()
        .then(() => new Promise((resolve) => {
            let settled = false;
            const finish = (ok) => {
                if (settled) {
                    return;
                }
                settled = true;
                clearTimeout(timer);
                resolve(ok);
            };

            const timer = setTimeout(() => {
                try {
                    window.google?.accounts?.id?.cancel?.();
                } catch (_err) {
                    /* ignore */
                }
                finish(false);
            }, ONE_TAP_PROMPT_TIMEOUT_MS);

            /**
             * @param {{credential?: string}} response
             */
            const handleCredentialResponse = (response) => {
                const credential = response?.credential;
                if (!credential) {
                    finish(false);
                    return;
                }
                completeGoogleOneTapSession({credential, nextPath})
                    .then((ok) => finish(ok))
                    .catch((err) => {
                        console.warn('Google One Tap session complete failed', err);
                        finish(false);
                    });
            };

            try {
                window.google.accounts.id.initialize({
                    client_id: clientId,
                    callback: handleCredentialResponse,
                    context: 'signin',
                    auto_select: true,
                    cancel_on_tap_outside: true,
                    itp_support: true,
                    use_fedcm_for_prompt: true,
                    ...(email ? {login_hint: email} : {}),
                });
            } catch (err) {
                console.warn('Google One Tap initialize failed', err);
                finish(false);
                return;
            }

            try {
                window.google.accounts.id.prompt((notification) => {
                    try {
                        if (notification.isNotDisplayed?.()) {
                            console.info(
                                'Google One Tap not displayed:',
                                notification.getNotDisplayedReason?.()
                            );
                            finish(false);
                            return;
                        }
                        if (notification.isSkippedMoment?.()) {
                            console.info(
                                'Google One Tap skipped:',
                                notification.getSkippedReason?.()
                            );
                            finish(false);
                            return;
                        }
                        if (notification.isDismissedMoment?.()) {
                            const reason = notification.getDismissedReason?.() || '';
                            if (reason === 'credential_returned') {
                                // Callback owns session complete + finish().
                                clearTimeout(timer);
                                return;
                            }
                            finish(false);
                            return;
                        }
                        if (notification.isDisplayed?.()) {
                            clearTimeout(timer);
                        }
                    } catch (err) {
                        console.warn('Google One Tap notification handler error', err);
                        finish(false);
                    }
                });
            } catch (err) {
                console.warn('Google One Tap prompt failed', err);
                finish(false);
            }
        }))
        .catch((err) => {
            console.warn('Google One Tap unavailable', err);
            return false;
        });
}

/**
 * POST credential to Django; on success swap adminbar Login → avatar in place.
 * @param {{credential: string, nextPath?: string}} opts
 * @returns {Promise<boolean>}
 */
function completeGoogleOneTapSession({credential, nextPath = '/'} = {}) {
    const csrf = ensureGsiCsrfToken();
    const body = new URLSearchParams();
    body.set('credential', credential);
    body.set('g_csrf_token', csrf);
    if (nextPath && nextPath !== '/') {
        body.set('next', nextPath);
    }

    return fetch(GOOGLE_ONETAP_LOGIN, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json',
        },
        body: body.toString(),
    })
        .then((res) => res.json().then((data) => ({res, data})))
        .then(({res, data}) => {
            if (!res.ok || !data?.valid || !data.html) {
                return false;
            }
            applyAuthenticatedAdminbar(data);
            saveLastLogin({email: data.email || '', idp: 'google'});
            dismissLastLoginPrompt();
            document.querySelector('#last-login-prompt')?.remove();
            return true;
        });
}

/**
 * Replace the Login control with the server-rendered account menu and set globals.
 * @param {object} data
 */
function applyAuthenticatedAdminbar(data) {
    const loginLi = document.querySelector('#adminbar li[data-name="social-login"]');
    const spacer = document.querySelector('#adminbar li.ab-flex-spacer');
    const presence = document.querySelector('#adminbar #presence-avatars-li');
    const mountAfter = presence || spacer;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = String(data.html || '').trim();
    const accountLi = wrapper.querySelector('li.account');
    if (!accountLi) {
        return;
    }

    if (loginLi) {
        loginLi.replaceWith(accountLi);
    } else if (mountAfter?.parentNode) {
        mountAfter.insertAdjacentElement('afterend', accountLi);
    } else {
        document.querySelector('#adminbar ul')?.appendChild(accountLi);
    }

    window.django_user_id = data.django_user_id != null ? String(data.django_user_id) : '';
    window.username = data.username || '';
    window.user_email = data.email || '';
    window.user_full_name = data.full_name || data.username || '';
    window.profile_id = data.profile_id != null ? String(data.profile_id) : '';
    window.profile_type = data.profile_type || '';
    window.profile_client_id = data.profile_client_id != null ? String(data.profile_client_id) : '';
    window.profile_idp = data.profile_idp || 'google';
    window.google_oauth_client_id = '';
}

/**
 * Ensure a g_csrf_token cookie exists and return its value (for PSA One Tap).
 * @returns {string}
 */
function ensureGsiCsrfToken() {
    const existing = readCookie('g_csrf_token');
    if (existing) {
        return existing;
    }
    const token = (window.crypto?.randomUUID?.() || `${Date.now()}.${Math.random()}`)
        .replace(/-/g, '')
        .slice(0, 32);
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `g_csrf_token=${token}; path=/; SameSite=Lax${secure}`;
    return token;
}

/**
 * @param {string} name
 * @returns {string}
 */
function readCookie(name) {
    const prefix = `${name}=`;
    const parts = document.cookie.split(';');
    for (let i = 0; i < parts.length; i += 1) {
        const part = parts[i].trim();
        if (part.startsWith(prefix)) {
            return decodeURIComponent(part.slice(prefix.length));
        }
    }
    return '';
}

/**
 * Build and show the bottom re-login prompt.
 * @param {{email: string, idp: string} | null} last
 * @param {{lead: string, onDismiss: () => void}} opts
 */
function mountLastLoginPrompt(last, {lead, onDismiss}) {
    if (document.querySelector('#last-login-prompt')) {
        return;
    }

    const idpLabel = last ? idpDisplayName(last.idp) : '';
    const prompt = document.createElement('div');
    prompt.id = 'last-login-prompt';
    prompt.className = 'last-login-prompt';
    prompt.setAttribute('role', 'dialog');
    prompt.setAttribute('aria-live', 'polite');
    prompt.setAttribute('aria-label', 'Sign in again');

    const message = document.createElement('p');
    message.className = 'last-login-prompt-message';
    if (last) {
        message.append(lead);
        const emailEl = document.createElement('strong');
        emailEl.textContent = last.email;
        message.append(emailEl);
        message.append(` with ${idpLabel}?`);
    } else {
        message.textContent = lead;
    }

    const actions = document.createElement('div');
    actions.className = 'last-login-prompt-actions';

    const yesBtn = document.createElement('button');
    yesBtn.type = 'button';
    yesBtn.className = 'last-login-prompt-yes';
    yesBtn.textContent = 'Yes, sign in';

    const noBtn = document.createElement('button');
    noBtn.type = 'button';
    noBtn.className = 'last-login-prompt-no';
    noBtn.textContent = 'Not now';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'last-login-prompt-close';
    closeBtn.setAttribute('aria-label', 'Dismiss');
    closeBtn.innerHTML = '&times;';

    const removePrompt = () => {
        prompt.classList.remove('show');
        setTimeout(() => prompt.remove(), 300);
    };

    const dismiss = () => {
        onDismiss();
        removePrompt();
    };

    yesBtn.addEventListener('click', () => {
        yesBtn.disabled = true;
        noBtn.disabled = true;
        const nextPath = `${location.pathname}${location.search}${location.hash}`;

        if (last && last.idp === 'google') {
            dismissLastLoginPrompt();
            removePrompt();
            tryGoogleOneTap({email: last.email, nextPath})
                .then((handled) => {
                    if (!handled) {
                        goToLoginWithAuto(nextPath);
                    }
                });
            return;
        }

        goToLoginWithAuto(nextPath);
    });
    noBtn.addEventListener('click', dismiss);
    closeBtn.addEventListener('click', dismiss);

    actions.append(yesBtn, noBtn);
    prompt.append(message, actions, closeBtn);
    document.body.append(prompt);
    requestAnimationFrame(() => prompt.classList.add('show'));
}

/**
 * Build and show the bottom re-login prompt for anonymous visitors.
 */
export function maybeShowLastLoginPrompt() {
    if (window.username || isOnLoginPage() || isPromptDismissed()) {
        return;
    }
    const last = getLastLogin();
    if (!last) {
        return;
    }
    mountLastLoginPrompt(last, {
        lead: 'Welcome back. Sign in again as ',
        onDismiss: dismissLastLoginPrompt,
    });
}

/**
 * Slide up the same panel when a signed-in page learns the session is gone.
 * The rendered page still has window.username, so the anonymous prompt stays hidden.
 */
export function showReauthPrompt() {
    if (isOnLoginPage() || reauthDismissed) {
        return;
    }
    syncLastLoginFromAuthenticatedSession();
    const last = getLastLogin();
    mountLastLoginPrompt(last, {
        lead: last
            ? 'Your session expired. Sign in again as '
            : 'Your session expired. Sign in again to continue.',
        onDismiss: () => {
            reauthDismissed = true;
        },
    });
}

/**
 * On /account/login/, auto-open the matching IdP control when armed.
 * @returns {boolean} true if an auto action was started
 */
export function applyLastLoginAutoOnLoginPage() {
    if (!consumeLastLoginAuto()) {
        return false;
    }
    const last = getLastLogin();
    if (!last) {
        return false;
    }

    if (last.idp === 'google') {
        const googleForm = document.querySelector(
            '.continue-card form[action*="google-oauth2"]'
        );
        const submitBtn = googleForm?.querySelector('button[type="submit"]');
        if (submitBtn) {
            submitBtn.click();
            return true;
        }
        if (googleForm) {
            googleForm.submit();
            return true;
        }
        return false;
    }

    if (last.idp === 'parkranger') {
        const emailInput = document.querySelector('#parkranger-email');
        if (emailInput) {
            emailInput.value = last.email;
        }
        const openBtn = document.querySelector('.open-parkranger-login');
        if (openBtn) {
            openBtn.click();
            return true;
        }
        return false;
    }

    return false;
}

function pageSetup() {
    syncLastLoginFromAuthenticatedSession();
    // Wait for #stop-wait / other boot overlays so the bottom prompt is clickable.
    const tryShow = () => {
        const stopWait = document.querySelector('#stop-wait');
        if (stopWait && stopWait.classList.contains('showing')) {
            setTimeout(tryShow, 250);
            return;
        }
        maybeShowLastLoginPrompt();
    };
    setTimeout(tryShow, 400);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pageSetup);
} else {
    pageSetup();
}

//# debugId=3fb305dd-514f-556a-9560-1fe9d2cc3fc8
